import React from "react";
import {
  Building2,
  Users,
  Folder,
  History,
  LayoutDashboard,
  CreditCard,
  KeyRound,
  Shield,
} from "lucide-react";
import { SettingsAccent } from "../components/settingsConfig";

export { WORKSPACE_PRODUCT_NAV } from "components/common/Sidebar/navContext";

export type WorkspaceSettingsSection = {
  id: string;
  title: string;
  description: string;
  path: string;
  icon: React.ReactNode;
  accent: SettingsAccent;
  priority: "high" | "medium" | "low";
  category?: string;
};

export type WorkspaceSettingsCategory = {
  id: string;
  label: string;
  icon: React.ReactNode;
  sections: WorkspaceSettingsSection[];
};

export const WORKSPACE_SETTINGS_SECTIONS: WorkspaceSettingsSection[] = [
  {
    id: "general",
    title: "Overview",
    description: "Overview of your workspace, team, licenses, and RivetAI usage",
    icon: <LayoutDashboard className="w-4 h-4" />,
    path: "/workspaces/:workspaceId/settings",
    accent: "blue",
    priority: "high",
    category: "general",
  },
  {
    id: "account",
    title: "Account",
    description: "Manage workspace identity and branding",
    icon: <Building2 className="w-4 h-4" />,
    path: "/workspaces/:workspaceId/settings/account",
    accent: "indigo",
    priority: "high",
    category: "management",
  },
  {
    id: "members",
    title: "Members",
    description: "Manage workspace members and permissions",
    icon: <Users className="w-4 h-4" />,
    path: "/workspaces/:workspaceId/settings/members",
    accent: "violet",
    priority: "high",
    category: "management",
  },
  {
    id: "billing",
    title: "Billing",
    description: "View charges and receipts; update your card or billing details when needed",
    icon: <CreditCard className="w-4 h-4" />,
    path: "/workspaces/:workspaceId/settings/billing",
    accent: "emerald",
    priority: "high",
    category: "management",
  },
  {
    id: "projects",
    title: "Project access",
    description: "Manage workspace projects and member access",
    icon: <Folder className="w-4 h-4" />,
    path: "/workspaces/:workspaceId/settings/projects",
    accent: "brand",
    priority: "high",
    category: "management",
  },
  {
    id: "api-keys",
    title: "Connections",
    description: "Connect Claude and manage scoped API keys for automation and external access",
    icon: <KeyRound className="w-4 h-4" />,
    path: "/workspaces/:workspaceId/settings/api-keys",
    accent: "teal",
    priority: "high",
    category: "activity",
  },
  {
    id: "sso",
    title: "Single sign-on",
    description: "Configure enterprise OIDC SSO for verified email domains",
    icon: <Shield className="w-4 h-4" />,
    path: "/workspaces/:workspaceId/settings/sso",
    accent: "indigo",
    priority: "high",
    category: "management",
  },
  {
    id: "activity",
    title: "Activity log",
    description: "View workspace activity and audit logs",
    icon: <History className="w-4 h-4" />,
    path: "/workspaces/:workspaceId/settings/activity",
    accent: "cyan",
    priority: "medium",
    category: "activity",
  },
];

export const WORKSPACE_SETTINGS_CATEGORIES: WorkspaceSettingsCategory[] = [
  {
    id: "general",
    label: "Overview",
    icon: <LayoutDashboard className="h-full w-full" />,
    sections: WORKSPACE_SETTINGS_SECTIONS.filter((s) => s.category === "general"),
  },
  {
    id: "management",
    label: "Workspace Management",
    icon: <Building2 className="h-full w-full" />,
    sections: WORKSPACE_SETTINGS_SECTIONS.filter((s) => s.category === "management"),
  },
  {
    id: "activity",
    label: "Activity & Logs",
    icon: <History className="h-full w-full" />,
    sections: WORKSPACE_SETTINGS_SECTIONS.filter((s) => s.category === "activity"),
  },
];

export const WORKSPACE_SETTINGS_SECTIONS_MAP: Record<string, WorkspaceSettingsSection> =
  Object.fromEntries(WORKSPACE_SETTINGS_SECTIONS.map((section) => [section.id, section])) as Record<
    string,
    WorkspaceSettingsSection
  >;

export const getWorkspaceSection = (id: string): WorkspaceSettingsSection | undefined =>
  WORKSPACE_SETTINGS_SECTIONS_MAP[id];

export const getWorkspaceAccent = (id: string) => getWorkspaceSection(id)?.accent;
